import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Zap, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open ? "backdrop-blur-xl bg-[#0A0A0B]/80 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-[0_0_20px_rgba(0,229,255,0.3)]">
            <Zap className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="text-xl font-semibold tracking-tight">
            Moxizo<span className="text-cyan-400">.</span>
          </span>
        </Link>

        {/* Desktop Nav - Pill Shape Container */}
        <div className="hidden md:flex items-center gap-4 bg-white/[0.03] border border-white/5 rounded-full px-2 py-1.5 backdrop-blur-md">
          <nav className="flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-1.5 text-sm rounded-full transition-all duration-300 ${
                    isActive ? "text-black font-semibold" : "text-gray-300 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.div
                        layoutId="nav-highlight"
                        className="absolute inset-0 bg-cyan-400 rounded-full"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10">{l.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          
          <div className="h-6 w-[1px] bg-white/10" />
          
          <Link
            to="/contact"
            className="btn-primary text-[13px] !py-1.5 !px-5 shadow-lg"
          >
            Book Consultation
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden w-10 h-10 rounded-full border border-white/10 flex items-center justify-center transition-all hover:bg-white/5 active:scale-95"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5 text-cyan-400" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0A0B]/95 backdrop-blur-2xl"
          >
            <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col gap-3">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 + 0.1 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-5 py-4 rounded-2xl transition-all duration-300 ${
                        isActive 
                          ? "bg-cyan-400 text-black font-bold shadow-[0_0_30px_rgba(0,229,255,0.2)]" 
                          : "text-gray-300 hover:bg-white/5 border border-transparent hover:border-white/5"
                      }`
                    }
                  >
                    <span className="text-lg tracking-tight">{l.label}</span>
                    <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-4"}`} />
                  </NavLink>
                </motion.div>
              ))}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: links.length * 0.05 + 0.2 }}
                className="mt-6"
              >
                <Link
                  to="/contact"
                  className="w-full btn-primary !py-4 flex justify-center text-base font-bold shadow-[0_20px_40px_rgba(0,229,255,0.15)]"
                >
                  Book Consultation
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
